import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { formatApiErrorDetail } from "@/lib/api";
import { AUTH } from "@/constants/testIds";

export default function Login() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const nextPath = params.get("next");
  const { login } = useAuth();
  const { t } = useI18n();
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(identifier.trim(), password);
      nav(nextPath && nextPath.startsWith("/") ? nextPath : "/dashboard");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Login failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#FAFAF7] flex items-center justify-center px-6">
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md bg-white rounded-3xl p-10 premium-shadow">
        <div className="flex items-center justify-between">
          <div className="text-xs font-semibold tracking-[0.24em] uppercase text-emerald-700">{t("auth.welcome_back")}</div>
          <LanguageSwitcher />
        </div>
        <h1 className="mt-2 text-4xl font-bold tracking-tight text-gray-900">{t("auth.signin")}</h1>
        <p className="mt-1 text-gray-600 text-sm">{t("auth.continue")}</p>

        <form onSubmit={submit} className="mt-8 space-y-4">
          <div>
            <label className="text-xs font-semibold text-gray-600 tracking-wide">{t("auth.mobile_or_email")}</label>
            <input data-testid={AUTH.loginIdentifier} value={identifier} onChange={(e) => setIdentifier(e.target.value)}
              className="mt-1 w-full rounded-xl border border-gray-200 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-emerald-500" />
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-600 tracking-wide">{t("auth.password")}</label>
            <input data-testid={AUTH.loginPassword} type="password" value={password} onChange={(e) => setPassword(e.target.value)}
              className="mt-1 w-full rounded-xl border border-gray-200 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-emerald-500" />
          </div>
          <button data-testid={AUTH.loginSubmit} disabled={busy}
            className="w-full py-4 rounded-full bg-emerald-600 text-white font-semibold hover:bg-emerald-700 disabled:opacity-60 shadow-xl">
            {busy ? "Signing in..." : t("auth.signin")}
          </button>
          <div className="text-center text-sm text-gray-600">
            <Link to="/forgot" className="text-emerald-700 font-medium">Forgot password?</Link>
          </div>
          <div className="text-center text-sm text-gray-600">
            {t("auth.new_to_gtf")} <Link data-testid={AUTH.toRegister} to="/role" className="text-emerald-700 font-medium">{t("auth.create_account")}</Link>
          </div>
        </form>
      </motion.div>
    </div>
  );
}
